import {
  Box,
  Button,
  ToastOptions as ChakraToastOptions,
  Toaster as ChakraToaster,
  CreateToasterReturn,
  Portal,
  Spinner,
  Stack,
  Toast,
  createToaster
} from "@chakra-ui/react"
import type { IconName } from "@pathwright/pathicons"
import type { ComponentProps, ElementType, ReactNode } from "react"
import { exhaustiveGuard } from "../../../utils/typing"
import Pathicon from "../../pathicon/Pathicon"

// NOTE: proper types are not beting set on the toaster for some reason, so we're
// manually typing things here.
type ToastType = "info" | "success" | "warning" | "error" | "loading"

// Match Chakra's colored toast backgrounds; info and loading stay neutral.
const toastActionColors: Record<string, string> = {
  success: "green",
  error: "red",
  warning: "orange"
}

export interface ToastOptions {
  title?: string
  description?: string | ReactNode
  type?: ToastType
  /** Replaces the icon implied by `type`. */
  icon?: IconName
  duration?: number
  closable?: boolean
  action?: {
    label: string
    onClick?: () => void
  }
  onStatusChange?: (details: { status: string }) => void
  placement?:
    | "top"
    | "top-start"
    | "top-end"
    | "bottom"
    | "bottom-start"
    | "bottom-end"
}

type ToasterType = Omit<
  CreateToasterReturn,
  "create" | "update" | "promise"
> & {
  create: (options: ToastOptions) => string
  dismiss: (id?: string) => void
  update: (id: string, options: Partial<ToastOptions>) => string
  promise: CreateToasterReturn["promise"]
}

const toastStore = createToaster({
  placement: "bottom-end",
  pauseOnPageIdle: true
})

export const toaster = toastStore as ToasterType

type ChakraToasterProps = Omit<
  ComponentProps<typeof ChakraToaster>,
  "children" | "insetInline" | "toaster"
> & {
  toaster: CreateToasterReturn
  insetInline?: string | { mdDown?: string }
  children: (toast: ChakraToastOptions) => ReactNode
}

const RenderableChakraToaster = (props: ChakraToasterProps) => (
  <ChakraToaster {...props} />
)

type ToastSlotProps<T extends ElementType> = ComponentProps<T> & {
  children?: ReactNode
}

// Note: We wrap Chakra Toast slot components so TypeScript accepts children in JSX

const ToastRoot = (props: ToastSlotProps<typeof Toast.Root>) => (
  <Toast.Root {...props} />
)

const ToastIndicator = (props: ToastSlotProps<typeof Toast.Indicator>) => (
  <Toast.Indicator {...props} />
)

const ToastTitle = (props: ToastSlotProps<typeof Toast.Title>) => (
  <Toast.Title {...props} />
)

const ToastDescription = (props: ToastSlotProps<typeof Toast.Description>) => (
  <Toast.Description {...props} />
)

const ToastActionTrigger = (
  props: ToastSlotProps<typeof Toast.ActionTrigger>
) => <Toast.ActionTrigger {...props} />

function ToastPathicon({ icon }: { icon: IconName }) {
  return (
    // Reserve space while Pathicon loads so the toast measures its full height.
    <Box boxSize="5" flexShrink={0}>
      <Pathicon icon={icon} color="inherit" boxSize="full" />
    </Box>
  )
}

function ToastIndicatorIcon({
  type
}: {
  type: Exclude<ToastType, "info" | "loading">
}) {
  switch (type) {
    case "success":
      return <Pathicon icon="check" color="inherit" />
    case "error":
      return <Pathicon icon="caution-circle" color="inherit" />
    case "warning":
      return <Pathicon icon="caution-triangle" color="inherit" />
    default:
      exhaustiveGuard(type)
  }
}

export const Toaster = () => {
  return (
    <Portal>
      <RenderableChakraToaster
        toaster={toastStore}
        insetInline={{ mdDown: "4" }}
      >
        {(toast: ChakraToastOptions) => {
          const icon = (toast as ToastOptions).icon
          return (
            <ToastRoot width={{ md: "sm" }}>
              {toast.type === "loading" ? (
                <Spinner size="sm" color="blue.solid" />
              ) : icon || toast.type === "info" ? (
                // Chakra's indicator only renders success, warning, and error
                // icons, and it replaces children with its own glyph.
                <ToastPathicon icon={icon ?? "info-circle"} />
              ) : (
                <ToastIndicator>
                  <ToastIndicatorIcon
                    type={toast.type as Exclude<ToastType, "info" | "loading">}
                  />
                </ToastIndicator>
              )}
              <Stack gap="1" flex="1" minWidth={0}>
                {toast.title && <ToastTitle>{toast.title}</ToastTitle>}
                {toast.description && (
                  <ToastDescription>{toast.description}</ToastDescription>
                )}
                {toast.action && (
                  <ToastActionTrigger asChild unstyled>
                    <Button
                      size="sm"
                      variant="subtle"
                      colorPalette={
                        toastActionColors[toast.type || ""] || "gray"
                      }
                      alignSelf="flex-start"
                      mt={2}
                    >
                      {toast.action.label}
                    </Button>
                  </ToastActionTrigger>
                )}
              </Stack>
              {toast.closable && <Toast.CloseTrigger />}
            </ToastRoot>
          )
        }}
      </RenderableChakraToaster>
    </Portal>
  )
}
